import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Key, Phone, Eye, EyeOff, Save, AlertCircle, RefreshCw, Trash2, Shield, Plus, Globe, CheckCircle, XCircle, Loader2, Download, Upload, Database, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { fetchSettings, updateSetting, testVoipConnection, downloadBackup, uploadBackup, getApiKeys, createApiKey, updateApiKey, deleteApiKey, regenerateApiKey } from "../../services/crmServices/settingService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { fetchAvailableDesks } from "../../services/crmServices/deskService";
import { fetchAvailableUsers } from "../../services/crmServices/userService";
import { Select } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../hooks/useAuth";
import { initiateSquareTalkCall, initiateVoisoCall, initiateCommpeakCall, initiateCoperatoCall, initiateGlobalCallCall, initiateBubbleVoipCall, getSquareTalkCallStatus, endCall } from "../../services/voipServices/voipService";
import Swal from "sweetalert2";

export function SystemSettings() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showApiKeys, setShowApiKeys] = useState({
        voiso: false,
        commpeak: false,
        coperato: false,
        squaretalk: false,
        globalcall: false,
        bubblevoip: false
    });

    const [apiKeys, setApiKeys] = useState({
        voiso: {
            apiKey: "",
            domain: "",
            enabled: false
        },
        commpeak: {
            apiKey: "",
            domain: "",
            crmId: "",
            clientId: "",
            c2cId: "", // Deprecated - kept for backward compatibility
            routeNumber: "00",
            prefix: "00",
            enabled: false
        },
        coperato: {
            domain: "",
            enabled: false
        },
        squaretalk: {
            apiKey: "",
            domain: "",
            accountId: "",
            enabled: false
        },
        globalcall: {
            baseUrl: "https://pbx.globalcall.tel/panel/api/click2call/",
            enabled: false
        },
        bubblevoip: {
            baseUrl: "http://bubble.voip247.cloud/c2c/call.php",
            enabled: false
        }
    });

    const [whiteIps, setWhiteIps] = useState([]);
    const [newIp, setNewIp] = useState("");
    const [originalApiKeys, setOriginalApiKeys] = useState({});
    const [originalWhiteIps, setOriginalWhiteIps] = useState([]);
    const [testingProvider, setTestingProvider] = useState(null);
    const [testResults, setTestResults] = useState({});
    const [backupLoading, setBackupLoading] = useState(false);
    const [restoreLoading, setRestoreLoading] = useState(false);
    
    // Test call states
    const [testCallLoading, setTestCallLoading] = useState(false);
    const [testCallResults, setTestCallResults] = useState({});
    const [testCallInputs, setTestCallInputs] = useState({
        agentExtension: "",
        destinationPhone: ""
    });
    const [callStatusPolling, setCallStatusPolling] = useState({}); // Track polling intervals
    const [callStatus, setCallStatus] = useState({}); // Current call status
    const [endingCall, setEndingCall] = useState(false); // Track end call loading state
    
    // Affiliate API Keys state
    const [affiliateApiKeys, setAffiliateApiKeys] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [mutedAffiliates, setMutedAffiliates] = useState([]);
    const [originalMutedAffiliates, setOriginalMutedAffiliates] = useState([]);
    const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
    const [editingApiKey, setEditingApiKey] = useState(null);
    const [newApiKeyData, setNewApiKeyData] = useState({
        name: "",
        defaultStatus: "New",
        isActive: true,
        autoAssign: { enabled: false, type: "", deskId: null, userId: null }
    });
    const [generatedApiKey, setGeneratedApiKey] = useState(null); // Store newly generated key for display
    const [apiKeyLoading, setApiKeyLoading] = useState(false);
    const [desks, setDesks] = useState([]);
    const [floorManagers, setFloorManagers] = useState([]);
    const [generalManagers, setGeneralManagers] = useState([]);
    const [editingAutoAssign, setEditingAutoAssign] = useState(null); // Track which key is being edited for auto-assign

    useEffect(() => {
        if (user.position.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
            return;
        }
        loadSettings();
        loadApiKeys();
        loadLeadStatuses();
        loadDesksAndUsers();
    }, [user.position]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await fetchSettings();
            
            // Define default providers (always use this as base)
            const defaultProviders = {
                voiso: { apiKey: "", domain: "", enabled: false },
                commpeak: { apiKey: "", domain: "", crmId: "", clientId: "", c2cId: "", routeNumber: "00", prefix: "00", enabled: false },
                coperato: { domain: "", enabled: false },
                squaretalk: { apiKey: "", domain: "", accountId: "", enabled: false },
                globalcall: { baseUrl: "https://pbx.globalcall.tel/panel/api/click2call/", enabled: false },
                bubblevoip: { baseUrl: "http://bubble.voip247.cloud/c2c/call.php", enabled: false }
            };
            
            if (response?.data?.length) {
                // Load VOIP settings - merge with defaults to preserve new providers
                if (response.data[0].voip) {
                    // Merge database settings with defaults
                    const mergedSettings = { ...defaultProviders };
                    Object.keys(response.data[0].voip).forEach(provider => {
                        if (mergedSettings[provider]) {
                            mergedSettings[provider] = {
                                ...mergedSettings[provider],
                                ...response.data[0].voip[provider]
                            };
                        } else {
                            mergedSettings[provider] = response.data[0].voip[provider];
                        }
                    });
                    
                    setApiKeys(mergedSettings);
                    setOriginalApiKeys(mergedSettings);
                } else {
                    // No voip settings in database yet, use defaults
                    setApiKeys(defaultProviders);
                    setOriginalApiKeys(defaultProviders);
                }

                // Load whitelist IPs
                if (response.data[0].whiteIps) {
                    setWhiteIps(response.data[0].whiteIps);
                    setOriginalWhiteIps(response.data[0].whiteIps);
                }

                // Load muted affiliates
                if (response.data[0].mutedAffiliates) {
                    setMutedAffiliates(response.data[0].mutedAffiliates);
                    setOriginalMutedAffiliates(response.data[0].mutedAffiliates);
                } else {
                    setMutedAffiliates([]);
                    setOriginalMutedAffiliates([]);
                }
            } else {
                // No settings in database yet, initialize with defaults
                setApiKeys(defaultProviders);
                setOriginalApiKeys(defaultProviders);
            }
        } catch (error) {
            console.error("Error loading settings:", error);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleApiKeyChange = (provider, field, value) => {
        setApiKeys((prev) => ({
            ...prev,
            [provider]: {
                ...prev[provider],
                [field]: value
            }
        }));
    };

    const handleSaveAPIKeys = async () => {
        setLoading(true);
        try {
            const response = await updateSetting({
                voip: apiKeys
            });

            if (response?.status === 200) {
                toast.success("API keys saved successfully");
                setOriginalApiKeys(apiKeys);
                setOriginalWhiteIps(whiteIps);
            } else {
                toast.error("Failed to save API keys");
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            toast.error("Failed to save settings");
        } finally {
            setLoading(false);
        }
    };

    const handleClearApiKey = (provider) => {
        if (
            window.confirm(
                `Are you sure you want to clear the ${provider} API key? This will disable ${provider} integration.`
            )
        ) {
            handleApiKeyChange(provider, "apiKey", "");
            handleApiKeyChange(provider, "enabled", false);
        }
    };

    const handleTestConnection = async (provider) => {
        setTestingProvider(provider);
        setTestResults((prev) => ({ ...prev, [provider]: null }));

        try {
            const providerSettings = apiKeys[provider] || {};
            const response = await testVoipConnection(provider, providerSettings);

            if (response?.success) {
                setTestResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: true,
                        message: response.message || `Successfully connected to ${provider}`,
                        details: response.data
                    }
                }));
                toast.success(`Connection test successful for ${provider}`);
                // Reload settings to get updated status
                loadSettings();
            } else {
                // Extract detailed error information
                const errorMessage = response.error || `Connection test failed for ${provider}`;
                const errorReason = response.reason || response.error;
                const troubleshooting = response.troubleshooting || {};
                const details = response.details || {};
                
                setTestResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: false,
                        message: errorMessage,
                        reason: errorReason,
                        details: details,
                        troubleshooting: troubleshooting,
                        endpoint: troubleshooting.endpoint,
                        accountId: troubleshooting.accountId,
                        squaretalkError: troubleshooting.squaretalkError,
                        squaretalkErrorCode: troubleshooting.squaretalkErrorCode
                    }
                }));
                
                // Show detailed error toast
                const toastMessage = errorReason || errorMessage;
                toast.error(toastMessage, {
                    duration: 8000, // Show longer for detailed errors
                    description: troubleshooting.squaretalkError ? `SquareTalk error: ${troubleshooting.squaretalkError}` : undefined
                });
            }
        } catch (error) {
            console.error(`Test connection error for ${provider}:`, error);
            const errorData = error?.response?.data || {};
            const errorMessage = errorData.error || error?.response?.data?.reason || error?.message || `Failed to test connection for ${provider}`;
            const errorReason = errorData.reason || errorData.error || errorMessage;
            const troubleshooting = errorData.troubleshooting || {};
            const details = errorData.details || {};

            setTestResults((prev) => ({
                ...prev,
                [provider]: {
                    success: false,
                    message: errorMessage,
                    reason: errorReason,
                    details: details,
                    troubleshooting: troubleshooting,
                    endpoint: troubleshooting.endpoint,
                    accountId: troubleshooting.accountId,
                    squaretalkError: troubleshooting.squaretalkError,
                    squaretalkErrorCode: troubleshooting.squaretalkErrorCode
                }
            }));
            
            // Show detailed error toast
            const toastMessage = errorReason || errorMessage;
            toast.error(toastMessage, {
                duration: 8000,
                description: troubleshooting.squaretalkError ? `SquareTalk error: ${troubleshooting.squaretalkError}` : undefined
            });
        } finally {
            setTestingProvider(null);
        }
    };

    const handleTestCall = async (provider) => {
        // Provider-specific validation
        if (provider === "globalcall") {
            if (!testCallInputs.globalcallSipAccount || !testCallInputs.globalcallPhone) {
                toast.error("Please enter both agent SIP account and destination phone number");
                return;
            }
        } else {
            if (!testCallInputs.agentExtension || !testCallInputs.destinationPhone) {
                toast.error("Please enter both agent extension and destination phone number");
                return;
            }
        }

        setTestCallLoading(true);
        setTestingProvider(provider); // Track which provider is being tested
        setTestCallResults((prev) => ({ ...prev, [provider]: null }));

        try {
            let callResult;
            // For test calls, pass extension directly instead of agentId (user ID)
            const callParams = {
                phoneNumber: testCallInputs.destinationPhone,
                extension: testCallInputs.agentExtension, // Pass extension directly for test calls
                leadId: null // Test call doesn't need a lead ID
            };

            // Call the appropriate provider's initiate function
            switch (provider) {
                case "squaretalk":
                    callResult = await initiateSquareTalkCall(callParams);
                    break;
                case "voiso":
                    callResult = await initiateVoisoCall(callParams);
                    break;
                case "commpeak":
                    callResult = await initiateCommpeakCall(callParams);
                    break;
                case "coperato":
                    callResult = await initiateCoperatoCall(callParams);
                    break;
                case "globalcall":
                    callResult = await initiateGlobalCallCall({
                        phoneNumber: testCallInputs.globalcallPhone,
                        agentId: user._id,
                        leadId: null, // Test call doesn't need a lead ID
                        agentSipAccount: testCallInputs.globalcallSipAccount,
                        clientName: "Test_Call"
                    });
                    break;
                case "bubblevoip":
                    callResult = await initiateBubbleVoipCall(callParams);
                    break;
                default:
                    toast.error("Provider not supported for test calls");
                    return;
            }

            if (callResult?.success) {
                const callId = callResult.callId || callResult.data?.call_id || callResult.data?.id || callResult.data?.callId;
                const fullResponse = callResult.data || callResult.fullResponse || callResult;
                const extension = testCallInputs.agentExtension;
                
                setTestCallResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: true,
                        message: callResult.message || "Call initiated successfully",
                        callId: callId,
                        extension: extension,
                        fullResponse: fullResponse,
                        rawResponse: callResult.rawResponse,
                        httpStatus: callResult.httpStatus,
                        note: callResult.note,
                        extensionStatus: callResult.extensionStatus,
                        description: callResult.description
                    }
                }));
                
                // Start polling for SquareTalk status
                if (provider === "squaretalk" && extension) {
                    startCallStatusPolling(provider, extension);
                }
                
                toast.success(`Test call initiated successfully for ${provider}${callId ? ` (Call ID: ${callId})` : ''}`, {
                    description: callResult.note || "Monitoring call status..."
                });
            } else {
                const errorMessage = callResult?.error || callResult?.message || "Failed to initiate test call";
                const errorDetails = callResult?.details || callResult?.responseBody || callResult;
                setTestCallResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: false,
                        message: errorMessage,
                        details: errorDetails,
                        endpoint: callResult?.endpoint,
                        requestBody: callResult?.requestBody,
                        httpStatus: callResult?.httpStatus
                    }
                }));
                toast.error(`Test call failed: ${errorMessage}`, {
                    duration: 10000,
                    description: errorDetails?.message || errorDetails?.error || "Check console for details"
                });
                console.error("[Test Call] Error details:", errorDetails);
            }
        } catch (error) {
            console.error(`Test call error for ${provider}:`, error);
            const errorMessage = error?.response?.data?.error || error?.message || `Failed to initiate test call for ${provider}`;
            setTestCallResults((prev) => ({
                ...prev,
                [provider]: {
                    success: false,
                    message: errorMessage
                }
            }));
            toast.error(`Test call failed: ${errorMessage}`);
        } finally {
            setTestCallLoading(false);
            setTestingProvider(null); // Clear provider tracking
        }
    };

    // Start polling call status for SquareTalk
    const startCallStatusPolling = (provider, extension) => {
        // Clear any existing polling for this provider
        if (callStatusPolling[provider]) {
            clearInterval(callStatusPolling[provider]);
        }

        // Initial status check
        checkCallStatus(provider, extension);

        // Poll every 3 seconds
        const interval = setInterval(() => {
            checkCallStatus(provider, extension);
        }, 3000);

        setCallStatusPolling((prev) => ({
            ...prev,
            [provider]: interval
        }));
    };

    // Stop polling call status
    const stopCallStatusPolling = (provider) => {
        if (callStatusPolling[provider]) {
            clearInterval(callStatusPolling[provider]);
            setCallStatusPolling((prev) => {
                const newPolling = { ...prev };
                delete newPolling[provider];
                return newPolling;
            });
        }
    };

    // Check call status
    const checkCallStatus = async (provider, extension) => {
        try {
            if (provider === "squaretalk" && extension) {
                const statusResult = await getSquareTalkCallStatus(extension);
                
                if (statusResult?.success) {
                    const statusInfo = statusResult.statusInfo || statusResult.data?.meta;
                    const extensionStatus = statusInfo?.extension_status || statusResult.data?.meta?.extension_status;
                    const connectivity = statusInfo?.connectivity || statusResult.data?.meta?.Connectivity;
                    const description = statusInfo?.description || statusResult.data?.meta?.description;
                    
                    // Get previous status before updating
                    const prevStatus = callStatus[provider]?.extensionStatus;
                    
                    setCallStatus((prev) => ({
                        ...prev,
                        [provider]: {
                            extensionStatus: extensionStatus,
                            connectivity: connectivity,
                            description: description,
                            status: statusResult.data?.status,
                            isConnected: statusResult.isConnected,
                            isRinging: statusResult.isRinging,
                            isBusy: statusResult.isBusy,
                            isDisconnected: statusResult.isDisconnected,
                            lastUpdated: new Date()
                        }
                    }));

                    // Update test call results with latest status
                    setTestCallResults((prev) => ({
                        ...prev,
                        [provider]: {
                            ...prev[provider],
                            extensionStatus: extensionStatus,
                            description: description,
                            connectivity: connectivity
                        }
                    }));

                    // Show toast notification for important status changes
                    // Only show toast if status actually changed
                    if (prevStatus && prevStatus !== extensionStatus) {
                        if (extensionStatus === "Connected" || statusResult.isConnected) {
                            toast.success("Call Answered", {
                                description: `Call is now connected. ${description || ''}`
                            });
                        } else if (extensionStatus === "DISCONNECTED" || statusResult.isDisconnected) {
                            toast.info("Call Ended", {
                                description: "The call has been disconnected"
                            });
                            stopCallStatusPolling(provider);
                        } else if (extensionStatus === "Busy") {
                            toast.warning("Extension Busy", {
                                description: "The extension is currently busy"
                            });
                        } else if (extensionStatus === "ringing" && prevStatus !== "ringing") {
                            toast.info("Extension Ringing", {
                                description: "Waiting for call to be answered..."
                            });
                        }
                    }

                    // Stop polling if call is disconnected or failed
                    // Continue polling for other statuses (ringing, Connected, Busy) to catch status changes
                    if (statusResult.isDisconnected || extensionStatus === "DISCONNECTED" || statusResult.data?.status === "failed") {
                        stopCallStatusPolling(provider);
                        if (prevStatus !== extensionStatus) {
                            toast.info("Status monitoring stopped", {
                                description: "Call has ended or failed"
                            });
                        }
                    }
                }
            }
        } catch (error) {
            // Don't stop polling on error, might be temporary network issue
        }
    };

    // Handle end call
    const handleEndCall = async (provider, extension) => {
        if (!extension) {
            toast.error("Extension is required to end the call");
            return;
        }

        setEndingCall(true);
        try {
            // For SquareTalk, we use extension as the callId
            const result = await endCall(extension, provider, extension);
            
            if (result?.success) {
                toast.success("Call Ended", {
                    description: "The call has been terminated successfully"
                });
                
                // Stop status polling
                stopCallStatusPolling(provider);
                
                // Update status to disconnected
                setCallStatus((prev) => ({
                    ...prev,
                    [provider]: {
                        ...prev[provider],
                        extensionStatus: "DISCONNECTED",
                        isDisconnected: true,
                        lastUpdated: new Date()
                    }
                }));
                
                // Update test call results
                setTestCallResults((prev) => ({
                    ...prev,
                    [provider]: {
                        ...prev[provider],
                        extensionStatus: "DISCONNECTED"
                    }
                }));
            } else {
                toast.error("Failed to End Call", {
                    description: result?.error || "Could not terminate the call"
                });
            }
        } catch (error) {
            toast.error("Failed to End Call", {
                description: error.message || "An error occurred while ending the call"
            });
        } finally {
            setEndingCall(false);
        }
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            Object.values(callStatusPolling).forEach(interval => {
                if (interval) clearInterval(interval);
            });
        };
    }, [callStatusPolling]);

    // IP Whitelist functions
    const validateIp = (ip) => {
        const ipRegex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        const cidrRegex =
            /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/([1-9]|[1-2][0-9]|3[0-2])$/;

        return ipRegex.test(ip) || cidrRegex.test(ip);
    };

    const handleAddIp = () => {
        const trimmedIp = newIp.trim();

        if (!trimmedIp) {
            toast.error("Please enter an IP address");
            return;
        }

        if (!validateIp(trimmedIp)) {
            toast.error("Please enter a valid IP address (e.g., 192.168.1.1 or 192.168.1.0/24)");
            return;
        }

        if (whiteIps.includes(trimmedIp)) {
            toast.error("This IP address is already in the whitelist");
            return;
        }
        const newWhiteIps = [...whiteIps, trimmedIp];
        updateSetting({ whiteIps: newWhiteIps }).then((response) => {
            if (response?.status === 200) {
                setWhiteIps(newWhiteIps);
                setNewIp("");
                toast.success("IP address added to whitelist");
            } else {
                toast.error("Failed to update IP whitelist");
                return;
            }
        });
    };

    const handleRemoveIp = (ipToRemove) => {
        if (window.confirm(`Are you sure you want to remove ${ipToRemove} from the whitelist?`)) {
            const newWhiteIps = whiteIps.filter((ip) => ip !== ipToRemove);
            updateSetting({ whiteIps: newWhiteIps }).then((response) => {
                if (response?.status === 200) {
                    setWhiteIps(newWhiteIps);
                    toast.success("IP address removed from whitelist");
                } else {
                    toast.error("Failed to remove IP from whitelist");
                }
            }).catch((error) => {
                console.error("Error removing IP:", error);
                toast.error("Failed to remove IP from whitelist");
            });
        }
    };

    const hasUnsavedChanges = () => {
        return (
            JSON.stringify(apiKeys) !== JSON.stringify(originalApiKeys) ||
            JSON.stringify(whiteIps) !== JSON.stringify(originalWhiteIps) ||
            JSON.stringify(mutedAffiliates.sort()) !== JSON.stringify(originalMutedAffiliates.sort())
        );
    };

    const handleToggleAffiliateNotification = (affiliateName) => {
        setMutedAffiliates((prev) => {
            if (prev.includes(affiliateName)) {
                // Unmute: remove from muted list
                return prev.filter((name) => name !== affiliateName);
            } else {
                // Mute: add to muted list
                return [...prev, affiliateName];
            }
        });
    };

    const handleSaveNotificationSettings = async () => {
        setLoading(true);
        try {
            const response = await updateSetting({
                mutedAffiliates: mutedAffiliates
            });

            if (response?.status === 200) {
                toast.success("Notification settings saved successfully");
                setOriginalMutedAffiliates(mutedAffiliates);
            } else {
                toast.error("Failed to save notification settings");
            }
        } catch (error) {
            console.error("Error saving notification settings:", error);
            toast.error("Failed to save notification settings");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadBackup = async () => {
        setBackupLoading(true);
        try {
            const response = await downloadBackup();
            
            if (response?.status === 200 && response?.data) {
                // Create blob and download
                const dataStr = JSON.stringify(response.data, null, 2);
                const dataBlob = new Blob([dataStr], { type: "application/json" });
                
                // Create download link
                const url = window.URL.createObjectURL(dataBlob);
                const link = document.createElement("a");
                link.href = url;
                
                // Generate filename with timestamp
                const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
                link.download = `crm-backup-${timestamp}.json`;
                
                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                toast.success("Database backup downloaded successfully!");
            } else {
                toast.error("Failed to create backup");
            }
        } catch (error) {
            console.error("Backup download error:", error);
            toast.error("Failed to download backup");
        } finally {
            setBackupLoading(false);
        }
    };

    const loadApiKeys = async () => {
        try {
            const response = await getApiKeys();
            if (response?.status === 200) {
                setAffiliateApiKeys(response.data || []);
            } else {
                console.error("Unexpected response:", response);
                // If no API keys exist yet, set empty array
                setAffiliateApiKeys([]);
            }
        } catch (error) {
            console.error("Error loading API keys:", error);
            // If it's a 404 or settings don't exist yet, just set empty array
            if (error.response?.status === 404 || error.response?.status === 400) {
                setAffiliateApiKeys([]);
            } else {
                toast.error("Failed to load API keys: " + (error.response?.data?.message || error.message));
            }
        }
    };

    const loadLeadStatuses = async () => {
        try {
            const response = await fetchLeadStatuses();
            if (response?.status === 200) {
                setLeadStatuses(response.data || []);
            }
        } catch (error) {
            console.error("Error loading lead statuses:", error);
        }
    };

    const loadDesksAndUsers = async () => {
        try {
            const [desksResponse, usersResponse] = await Promise.all([
                fetchAvailableDesks(),
                fetchAvailableUsers()
            ]);
            if (desksResponse?.status === 200) {
                setDesks(desksResponse.data || []);
            }
            if (usersResponse?.status === 200) {
                const users = usersResponse.data || [];
                // Filter users by role for floor managers and general managers
                setFloorManagers(users.filter(u => u.position?.role === "floorManager"));
                setGeneralManagers(users.filter(u => u.position?.role === "generalManager"));
            }
        } catch (error) {
            console.error("Error loading desks and users:", error);
        }
    };

    const handleCreateApiKey = async () => {
        if (!newApiKeyData.name.trim()) {
            toast.error("Affiliate name is required");
            return;
        }

        setApiKeyLoading(true);
        try {
            const response = await createApiKey(newApiKeyData);
            if (response?.status === 200) {
                toast.success("API key created successfully");
                setGeneratedApiKey(response.data.apiKey); // Store the generated key
                await loadApiKeys();
                // Keep modal open to show the key
            } else {
                toast.error(response?.message || "Failed to create API key");
            }
        } catch (error) {
            console.error("Error creating API key:", error);
            toast.error(error.response?.data?.message || "Failed to create API key");
        } finally {
            setApiKeyLoading(false);
        }
    };

    const handleCloseApiKeyModal = () => {
        setShowCreateApiKeyModal(false);
        setGeneratedApiKey(null);
        setNewApiKeyData({ name: "", defaultStatus: "New", isActive: true, autoAssign: { enabled: false, type: "", deskId: null, userId: null } });
    };

    const handleUpdateApiKey = async (keyId, updates) => {
        setApiKeyLoading(true);
        try {
            const response = await updateApiKey(keyId, updates);
            if (response?.status === 200) {
                toast.success("API key updated successfully");
                await loadApiKeys();
                setEditingApiKey(null);
            } else {
                toast.error(response?.message || "Failed to update API key");
            }
        } catch (error) {
            console.error("Error updating API key:", error);
            toast.error(error.response?.data?.message || "Failed to update API key");
        } finally {
            setApiKeyLoading(false);
        }
    };

    const handleDeleteApiKey = async (keyId) => {
        const result = await Swal.fire({
            title: "Delete API Key?",
            text: "This will permanently delete the API key. Affiliates using this key will no longer be able to access the API.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it"
        });

        if (result.isConfirmed) {
            setApiKeyLoading(true);
            try {
                const response = await deleteApiKey(keyId);
                if (response?.status === 200) {
                    toast.success("API key deleted successfully");
                    await loadApiKeys();
                } else {
                    toast.error(response?.message || "Failed to delete API key");
                }
            } catch (error) {
                console.error("Error deleting API key:", error);
                toast.error(error.response?.data?.message || "Failed to delete API key");
            } finally {
                setApiKeyLoading(false);
            }
        }
    };

    const handleRegenerateApiKey = async (keyId) => {
        const result = await Swal.fire({
            title: "Regenerate API Key?",
            text: "This will generate a new API key. The old key will no longer work. Make sure to update the affiliate with the new key.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, regenerate it"
        });

        if (result.isConfirmed) {
            setApiKeyLoading(true);
            try {
                const response = await regenerateApiKey(keyId);
                if (response?.status === 200) {
                    toast.success("API key regenerated successfully");
                    setGeneratedApiKey(response.data.apiKey);
                    setEditingApiKey(keyId);
                    await loadApiKeys();
                    // Show the new key
                    Swal.fire({
                        title: "New API Key Generated",
                        html: `<div class="text-left">
                            <p class="mb-2">Copy this key now. You won't be able to see it again!</p>
                            <div class="bg-gray-100 p-3 rounded font-mono text-sm break-all">${response.data.apiKey}</div>
                        </div>`,
                        icon: "success",
                        confirmButtonText: "I've copied it"
                    });
                } else {
                    toast.error(response?.message || "Failed to regenerate API key");
                }
            } catch (error) {
                console.error("Error regenerating API key:", error);
                toast.error(error.response?.data?.message || "Failed to regenerate API key");
            } finally {
                setApiKeyLoading(false);
            }
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    const handleUploadBackup = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Confirm before proceeding
        const result = await Swal.fire({
            title: "Restore Database?",
            html: `
                <p>You are about to restore the database from a backup file.</p>
                <p class="text-sm text-gray-600 mt-2"><strong>File:</strong> ${file.name}</p>
                <p class="text-sm text-red-600 mt-2"><strong>Warning:</strong> This operation will merge backup data with existing data. Duplicate IDs will be updated.</p>
                <p class="text-sm text-gray-600 mt-2">Make sure you have a recent backup before proceeding.</p>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Restore",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626",
            input: "radio",
            inputOptions: {
                merge: "Merge with existing data (recommended)",
                replace: "Replace all data (DANGEROUS - will delete everything first)"
            },
            inputValue: "merge",
            inputValidator: (value) => {
                if (!value) {
                    return "You need to choose a restore mode!";
                }
            }
        });

        if (!result.isConfirmed) {
            // Clear file input
            event.target.value = "";
            return;
        }

        const mode = result.value;

        setRestoreLoading(true);
        try {
            // Read file
            const fileContent = await file.text();
            const backupData = JSON.parse(fileContent);

            // Validate backup structure
            if (!backupData.data || !backupData.metadata) {
                toast.error("Invalid backup file format");
                return;
            }

            // Show info
            toast.info(`Restoring ${Object.keys(backupData.data).length} collections...`, {
                duration: 3000
            });

            // Upload backup
            const response = await uploadBackup(backupData, mode);

            if (response?.status === 200) {
                const summary = response.summary;
                await Swal.fire({
                    title: "Restore Complete!",
                    html: `
                        <div class="text-left">
                            <p class="text-green-600"><strong>✓ Successful:</strong> ${summary.successful} collections</p>
                            <p class="text-gray-600"><strong>○ Skipped:</strong> ${summary.skipped} collections (empty)</p>
                            ${summary.failed > 0 ? `<p class="text-red-600"><strong>✗ Failed:</strong> ${summary.failed} collections</p>` : ""}
                        </div>
                    `,
                    icon: summary.failed > 0 ? "warning" : "success"
                });
                
                // Reload settings
                loadSettings();
                
                // Suggest page reload
                const reloadResult = await Swal.fire({
                    title: "Reload Application?",
                    text: "It's recommended to reload the page to ensure all data is synchronized.",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Reload Now",
                    cancelButtonText: "Later"
                });
                
                if (reloadResult.isConfirmed) {
                    window.location.reload();
                }
            } else {
                toast.error("Failed to restore backup");
            }
        } catch (error) {
            console.error("Backup restore error:", error);
            if (error.message.includes("JSON")) {
                toast.error("Invalid backup file: Not a valid JSON file");
            } else {
                toast.error("Failed to restore backup: " + error.message);
            }
        } finally {
            setRestoreLoading(false);
            // Clear file input
            event.target.value = "";
        }
    };

    if (user.position.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <Card className="max-w-md mx-auto mt-8">
                    <CardContent className="text-center py-8">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                        <p className="text-gray-600">Administrator privileges required to access this page.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">System Settings</h1>
                        <p className="text-gray-600">Configure VOIP API keys and security settings</p>
                    </div>
                    <Button onClick={loadSettings} variant="outline" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Key className="h-5 w-5 text-blue-600" />
                            <div>
                                <p className="text-sm text-gray-600">VOIP Providers</p>
                                <p className="text-2xl font-bold">{Object.keys(apiKeys).length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Shield className="h-5 w-5 text-purple-600" />
                            <div>
                                <p className="text-sm text-gray-600">Whitelisted IPs</p>
                                <p className="text-2xl font-bold text-purple-600">{whiteIps.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* IP Whitelist Section */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                        <Shield className="h-5 w-5 text-purple-600" />
                        <span>IP Whitelist</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            <p className="text-sm text-yellow-800">
                                Only IP addresses in this whitelist will be allowed to access the system. Be careful
                                when modifying this list to avoid locking yourself out.
                            </p>
                        </div>
                    </div>

                    <div className="flex space-x-2">
                        <Input
                            value={newIp}
                            onChange={(e) => setNewIp(e.target.value)}
                            placeholder="Enter IP address (e.g., 192.168.1.1 or 192.168.1.0/24)"
                            onKeyPress={(e) => e.key === "Enter" && handleAddIp()}
                            className="flex-1"
                        />
                        <Button
                            onClick={handleAddIp}
                            disabled={!newIp.trim()}
                            className=""
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add IP
                        </Button>
                    </div>

                    {whiteIps.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {whiteIps.map((ip, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                                >
                                    <div className="flex items-center space-x-2">
                                        <Globe className="h-4 w-4 text-gray-500" />
                                        <span className="font-mono text-sm">{ip}</span>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleRemoveIp(ip)}
                                        className="text-red-600 hover:text-red-700"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                            <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">No IP addresses in whitelist</p>
                            <p className="text-sm text-gray-400">Add IP addresses to restrict system access</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* VOIP API Keys Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                        <Phone className="h-5 w-5 text-blue-600" />
                        <span>VOIP API Keys</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Voiso API Key */}
                    <div className="border rounded-lg p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">Voiso API Key</h3>
                                    <p className="text-sm text-gray-600">
                                        Configure Voiso integration for voice calling
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="voiso-api-key">API Key</Label>
                                <div className="flex space-x-2">
                                    <Input
                                        id="voiso-api-key"
                                        type={showApiKeys.voiso ? "text" : "password"}
                                        value={apiKeys.voiso.apiKey}
                                        onChange={(e) => handleApiKeyChange("voiso", "apiKey", e.target.value)}
                                        placeholder="Enter Voiso API Key"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowApiKeys((prev) => ({ ...prev, voiso: !prev.voiso }))}
                                    >
                                        {showApiKeys.voiso ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                    {apiKeys.voiso.apiKey && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleClearApiKey("voiso")}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="voiso-domain">Domain</Label>
                                <Input
                                    id="voiso-domain"
                                    type="text"
                                    value={apiKeys.voiso.domain}
                                    onChange={(e) => handleApiKeyChange("voiso", "domain", e.target.value)}
                                    placeholder="Enter Voiso Domain (e.g., your-company.voiso.com)"
                                    className="flex-1"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="voiso-enabled"
                                    checked={apiKeys.voiso.enabled}
                                    onChange={(e) => handleApiKeyChange("voiso", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="voiso-enabled">Enable Voiso Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("voiso")}
                                    disabled={testingProvider === "voiso" || !apiKeys.voiso.apiKey || !apiKeys.voiso.domain}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "voiso" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.voiso && (
                                    <div className={`flex items-center space-x-2 ${testResults.voiso.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.voiso.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.voiso.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.voiso && !testResults.voiso.success && testResults.voiso.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.voiso.reason}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Coperato Domain */}
                    <div className="border rounded-lg p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Globe className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">Coperato Domain</h3>
                                    <p className="text-sm text-gray-600">Configure Coperato domain for voice calling</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="coperato-domain">Domain</Label>
                                <Input
                                    id="coperato-domain"
                                    type="text"
                                    value={apiKeys.coperato?.domain}
                                    onChange={(e) => handleApiKeyChange("coperato", "domain", e.target.value)}
                                    placeholder="Enter Coperato Domain (e.g., your-company.coperato.com)"
                                    className="flex-1"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="coperato-enabled"
                                    checked={apiKeys.coperato?.enabled}
                                    onChange={(e) => handleApiKeyChange("coperato", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="coperato-enabled">Enable Coperato Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("coperato")}
                                    disabled={testingProvider === "coperato" || !apiKeys.coperato?.domain}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "coperato" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.coperato && (
                                    <div className={`flex items-center space-x-2 ${testResults.coperato.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.coperato.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.coperato.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.coperato && !testResults.coperato.success && testResults.coperato.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.coperato.reason}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SquareTalk API Key */}
                    <div className="border rounded-lg p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">SquareTalk API Key</h3>
                                    <p className="text-sm text-gray-600">
                                        Configure SquareTalk integration for voice calling
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="squaretalk-api-key">API Key</Label>
                                <div className="flex space-x-2">
                                    <Input
                                        id="squaretalk-api-key"
                                        type={showApiKeys.squaretalk ? "text" : "password"}
                                        value={apiKeys.squaretalk.apiKey}
                                        onChange={(e) => handleApiKeyChange("squaretalk", "apiKey", e.target.value)}
                                        placeholder="Enter SquareTalk API Key"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            setShowApiKeys((prev) => ({ ...prev, squaretalk: !prev.squaretalk }))
                                        }
                                    >
                                        {showApiKeys.squaretalk ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                    {apiKeys.squaretalk.apiKey && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleClearApiKey("squaretalk")}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="squaretalk-domain">Domain (Important for API access)</Label>
                                <Input
                                    id="squaretalk-domain"
                                    type="text"
                                    value={apiKeys.squaretalk?.domain || ""}
                                    onChange={(e) => handleApiKeyChange("squaretalk", "domain", e.target.value)}
                                    placeholder="Enter SquareTalk Domain or Custom API Domain"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Your SquareTalk custom domain or API domain. This is often required for API access. 
                                    Check your SquareTalk portal for your account's API domain. 
                                    If CloudFront errors occur, this field might be required.
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="squaretalk-account-id">Account ID (Optional)</Label>
                                <Input
                                    id="squaretalk-account-id"
                                    type="text"
                                    value={apiKeys.squaretalk?.accountId || ""}
                                    onChange={(e) => handleApiKeyChange("squaretalk", "accountId", e.target.value)}
                                    placeholder="Enter SquareTalk Account ID (optional - API token may contain account info)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Account ID may be optional if your API token contains account information. 
                                    If you get errors, try adding your Account ID from SquareTalk portal.
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="squaretalk-enabled"
                                    checked={apiKeys.squaretalk?.enabled || false}
                                    onChange={(e) => handleApiKeyChange("squaretalk", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="squaretalk-enabled">Enable SquareTalk Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("squaretalk")}
                                    disabled={testingProvider === "squaretalk" || !apiKeys.squaretalk.apiKey}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "squaretalk" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.squaretalk && (
                                    <div className={`flex items-center space-x-2 ${testResults.squaretalk.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.squaretalk.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.squaretalk.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.squaretalk && !testResults.squaretalk.success && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                                    <div>
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                        <p className="text-xs text-red-600 mt-1 whitespace-pre-line">{testResults.squaretalk.reason}</p>
                                </div>
                                    
                                    {testResults.squaretalk.troubleshooting && (
                                        <div className="mt-3 pt-3 border-t border-red-300">
                                            <p className="text-xs font-semibold text-red-900 mb-2">Technical Details:</p>
                                            <div className="text-xs text-red-700 space-y-1">
                                                {testResults.squaretalk.troubleshooting.endpoint && (
                                                    <p><span className="font-medium">Endpoint:</span> {testResults.squaretalk.troubleshooting.endpoint}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.accountId && (
                                                    <p><span className="font-medium">Account ID:</span> {testResults.squaretalk.troubleshooting.accountId}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.squaretalkError && (
                                                    <p><span className="font-medium">SquareTalk Error:</span> {testResults.squaretalk.troubleshooting.squaretalkError}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.squaretalkErrorCode && (
                                                    <p><span className="font-medium">Error Code:</span> {testResults.squaretalk.troubleshooting.squaretalkErrorCode}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.responseCode && (
                                                    <p><span className="font-medium">HTTP Status:</span> {testResults.squaretalk.troubleshooting.responseCode}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {testResults.squaretalk.details?.responseBody && (
                                        <div className="mt-3 pt-3 border-t border-red-300">
                                            <details className="text-xs">
                                                <summary className="cursor-pointer text-red-900 font-medium hover:text-red-700">
                                                    View Full API Response
                                                </summary>
                                                <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto">
                                                    {JSON.stringify(testResults.squaretalk.details.responseBody, null, 2)}
                                                </pre>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Test Call Section */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the SquareTalk integration by initiating a real call. Enter the agent extension and destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-agent-extension-squaretalk">Agent Extension</Label>
                                            <Input
                                                id="test-agent-extension-squaretalk"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="Enter agent extension (e.g., 1001)"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-squaretalk">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-squaretalk"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("squaretalk")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.squaretalk.apiKey}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.squaretalk && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.squaretalk.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.squaretalk.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.squaretalk.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.squaretalk.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.squaretalk.message && (
                                                    <p className={`text-xs ${testCallResults.squaretalk.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.squaretalk.message}
                                                    </p>
                                                )}
                                                {testCallResults.squaretalk.note && (
                                                    <p className="text-xs text-blue-600 mt-1">
                                                        Note: {testCallResults.squaretalk.note}
                                                    </p>
                                                )}
                                                {testCallResults.squaretalk.extension && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Extension: {testCallResults.squaretalk.extension}
                                                    </p>
                                                )}
                                                {/* Call Status Display - Auto-updates */}
                                                {testCallResults.squaretalk.success && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-xs font-semibold text-gray-700">Call Status:</p>
                                                            {callStatusPolling.squaretalk && (
                                                                <Badge variant="outline" className="text-xs">
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                    Monitoring...
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="space-y-2">
                                                            {/* Extension Status */}
                                                            {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) && (
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs text-gray-600">Extension Status:</span>
                                                                    <Badge 
                                                                        className={`text-xs font-semibold ${
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "ringing" ? "bg-yellow-100 text-yellow-800 border-yellow-300 animate-pulse" :
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Connected" || callStatus.squaretalk?.isConnected ? "bg-green-100 text-green-800 border-green-300" :
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Busy" ? "bg-orange-100 text-orange-800 border-orange-300" :
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "DISCONNECTED" ? "bg-red-100 text-red-800 border-red-300" :
                                                                            "bg-gray-100 text-gray-800 border-gray-300"
                                                                        }`}
                                                                    >
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "ringing" && "🔔 "}
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Connected" && "✅ "}
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "DISCONNECTED" && "❌ "}
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Busy" && "📞 "}
                                                                        {testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus}
                                                                    </Badge>
                                                                </div>
                                                            )}
                                                            {/* Connectivity Status */}
                                                            {callStatus.squaretalk?.connectivity && (
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs text-gray-600">Connectivity:</span>
                                                                    <Badge 
                                                                        className={`text-xs ${
                                                                            callStatus.squaretalk.connectivity === "Connected" ? "bg-green-100 text-green-800 border-green-300" : "bg-gray-100 text-gray-800 border-gray-300"
                                                                        }`}
                                                                    >
                                                                        {callStatus.squaretalk.connectivity}
                                                                    </Badge>
                                                                </div>
                                                            )}
                                                            {/* Description */}
                                                            {(testCallResults.squaretalk.description || callStatus.squaretalk?.description) && (
                                                                <p className="text-xs text-gray-600 mt-1 italic bg-gray-50 p-2 rounded">
                                                                    {testCallResults.squaretalk.description || callStatus.squaretalk?.description}
                                                                </p>
                                                            )}
                                                            {/* Last Updated */}
                                                            {callStatus.squaretalk?.lastUpdated && (
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    Last updated: {new Date(callStatus.squaretalk.lastUpdated).toLocaleTimeString()}
                                                                </p>
                                                            )}
                                                            {/* Action Buttons */}
                                                            <div className="flex space-x-2 mt-2">
                                                                {/* End Call Button */}
                                                                {testCallResults.squaretalk.success && 
                                                                 (testCallResults.squaretalk.extensionStatus === "ringing" || 
                                                                  testCallResults.squaretalk.extensionStatus === "Connected" ||
                                                                  callStatus.squaretalk?.extensionStatus === "ringing" ||
                                                                  callStatus.squaretalk?.extensionStatus === "Connected" ||
                                                                  callStatus.squaretalk?.isConnected) && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="destructive"
                                                                        size="sm"
                                                                        onClick={() => handleEndCall("squaretalk", testCallResults.squaretalk.extension || testCallInputs.agentExtension)}
                                                                        disabled={endingCall}
                                                                        className="flex-1 text-xs"
                                                                    >
                                                                        {endingCall ? (
                                                                            <>
                                                                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                                Ending...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Phone className="h-3 w-3 mr-1" />
                                                                                End Call
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                )}
                                                                {/* Stop Monitoring Button */}
                                                                {callStatusPolling.squaretalk && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => stopCallStatusPolling("squaretalk")}
                                                                        className="flex-1 text-xs"
                                                                    >
                                                                        Stop Monitoring
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Technical Details (Expandable) */}
                                                        <details className="mt-3 text-xs">
                                                            <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                Technical Details
                                                            </summary>
                                                            <div className="mt-2 space-y-1">
                                                                {testCallResults.squaretalk.httpStatus && (
                                                                    <p className="text-xs text-gray-600">
                                                                        HTTP Status: {testCallResults.squaretalk.httpStatus}
                                                                    </p>
                                                                )}
                                                                {testCallResults.squaretalk.fullResponse && (
                                                                    <details className="mt-2">
                                                                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                            View Full API Response
                                                                        </summary>
                                                                        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                            {JSON.stringify(testCallResults.squaretalk.fullResponse, null, 2)}
                                                                        </pre>
                                                                    </details>
                                                                )}
                                                            </div>
                                                        </details>
                                                    </div>
                                                )}
                                                {!testCallResults.squaretalk.success && testCallResults.squaretalk.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.squaretalk.endpoint && (
                                                                <p><span className="font-medium">Endpoint:</span> {testCallResults.squaretalk.endpoint}</p>
                                                            )}
                                                            {testCallResults.squaretalk.httpStatus && (
                                                                <p><span className="font-medium">HTTP Status:</span> {testCallResults.squaretalk.httpStatus}</p>
                                                            )}
                                                            {testCallResults.squaretalk.requestBody && (
                                                                <p><span className="font-medium">Request Body:</span> {JSON.stringify(testCallResults.squaretalk.requestBody)}</p>
                                                            )}
                                                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                {JSON.stringify(testCallResults.squaretalk.details, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CommPeak API Key */}
                    <div className="border rounded-lg p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">CommPeak API Key</h3>
                                    <p className="text-sm text-gray-600">
                                        Configure CommPeak integration for voice calling
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="commpeak-crm-id">CRM ID *</Label>
                                <Input
                                    id="commpeak-crm-id"
                                    type="text"
                                    value={apiKeys.commpeak?.crmId || ""}
                                    onChange={(e) => handleApiKeyChange("commpeak", "crmId", e.target.value)}
                                    placeholder="Enter CRM vendor ID (e.g., 76)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Unique CRM vendor ID (Static - provided by CommPeak). <strong>This is DIFFERENT from Client ID below.</strong> Both are required. If support gave you only one ID, ask them for both separately.
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="commpeak-client-id">Client ID *</Label>
                                <Input
                                    id="commpeak-client-id"
                                    type="text"
                                    value={apiKeys.commpeak?.clientId || apiKeys.commpeak?.c2cId || ""}
                                    onChange={(e) => {
                                        handleApiKeyChange("commpeak", "clientId", e.target.value);
                                        // Also update c2cId for backward compatibility
                                        if (!apiKeys.commpeak?.clientId && apiKeys.commpeak?.c2cId) {
                                            handleApiKeyChange("commpeak", "c2cId", e.target.value);
                                        }
                                    }}
                                    placeholder="Enter Click2Call Client ID (e.g., 1248)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Unique Click2Call ID (Static - provided by CommPeak). Also called C2C ID. <strong>This is DIFFERENT from CRM ID above.</strong> Both are required. If support gave you only one ID, ask them for both separately.
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="commpeak-route-number">Route Number (Prefix)</Label>
                                <Input
                                    id="commpeak-route-number"
                                    type="text"
                                    value={apiKeys.commpeak?.routeNumber || apiKeys.commpeak?.prefix || "00"}
                                    onChange={(e) => {
                                        handleApiKeyChange("commpeak", "routeNumber", e.target.value);
                                        handleApiKeyChange("commpeak", "prefix", e.target.value);
                                    }}
                                    placeholder="00"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    PBX outbound calls prefix (default: '00'). Must match your dial pattern configuration on Manual Dialing Routes.
                                </p>
                            </div>
                            
                            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                                <p className="text-xs text-blue-800">
                                    <strong>Note:</strong> CommPeak Click2Call always uses the standard domain <strong>click2call.pbx.commpeak.com</strong>. 
                                    The endpoint format is: <code className="bg-blue-100 px-1 rounded">https://click2call.pbx.commpeak.com/call/{'{crm_id}'}/{'{client_id}'}/{'{leg_a}'}/{'{leg_b}'}/{'{prefix}'}</code>
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="commpeak-enabled"
                                    checked={apiKeys.commpeak?.enabled || false}
                                    onChange={(e) => handleApiKeyChange("commpeak", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="commpeak-enabled">Enable CommPeak Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("commpeak")}
                                    disabled={testingProvider === "commpeak" || !apiKeys.commpeak?.crmId || (!apiKeys.commpeak?.clientId && !apiKeys.commpeak?.c2cId)}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "commpeak" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.commpeak && (
                                    <div className={`flex items-center space-x-2 ${testResults.commpeak.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.commpeak.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.commpeak.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.commpeak && !testResults.commpeak.success && testResults.commpeak.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.commpeak.reason}</p>
                                    {testResults.commpeak.troubleshooting && (
                                        <details className="mt-2 text-xs">
                                            <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                View Troubleshooting
                                            </summary>
                                            <div className="mt-2 space-y-1 p-2 bg-red-100 rounded">
                                                {testResults.commpeak.troubleshooting.suggestion && (
                                                    <p className="text-xs text-red-800">
                                                        <span className="font-medium">Suggestion:</span> {testResults.commpeak.troubleshooting.suggestion}
                                                    </p>
                                                )}
                                                {testResults.commpeak.troubleshooting.action && (
                                                    <p className="text-xs text-red-800 mt-1">
                                                        <span className="font-medium">Action:</span> {testResults.commpeak.troubleshooting.action}
                                                    </p>
                                                )}
                                                {testResults.commpeak.troubleshooting.errorCode && (
                                                    <p className="text-xs text-red-800 mt-1">
                                                        <span className="font-medium">Error Code:</span> {testResults.commpeak.troubleshooting.errorCode}
                                                    </p>
                                                )}
                                </div>
                                        </details>
                                    )}
                                </div>
                            )}

                            {/* Test Call Section for CommPeak */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the CommPeak integration by initiating a real call. Enter the agent extension and destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-agent-extension-commpeak">Agent Extension</Label>
                                            <Input
                                                id="test-agent-extension-commpeak"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="Enter agent extension (e.g., 1001)"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-commpeak">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-commpeak"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("commpeak")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.commpeak?.crmId || (!apiKeys.commpeak?.clientId && !apiKeys.commpeak?.c2cId)}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.commpeak && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.commpeak.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.commpeak.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.commpeak.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.commpeak.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.commpeak.message && (
                                                    <p className={`text-xs ${testCallResults.commpeak.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.commpeak.message}
                                                    </p>
                                                )}
                                                {testCallResults.commpeak.callId && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Call ID: {testCallResults.commpeak.callId}
                                                    </p>
                                                )}
                                                {testCallResults.commpeak.extension && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Extension: {testCallResults.commpeak.extension}
                                                    </p>
                                                )}
                                                {/* Error Details */}
                                                {!testCallResults.commpeak.success && testCallResults.commpeak.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.commpeak.details.errorCode && (
                                                                <p className="text-xs text-red-800">
                                                                    <span className="font-medium">Error Code:</span> {testCallResults.commpeak.details.errorCode}
                                                                </p>
                                                            )}
                                                            {testCallResults.commpeak.details.troubleshooting && (
                                                                <div className="mt-2 p-2 bg-red-100 rounded">
                                                                    {testCallResults.commpeak.details.troubleshooting.suggestion && (
                                                                        <p className="text-xs text-red-800">
                                                                            <span className="font-medium">Suggestion:</span> {testCallResults.commpeak.details.troubleshooting.suggestion}
                                                                        </p>
                                                                    )}
                                                                    {testCallResults.commpeak.details.troubleshooting.action && (
                                                                        <p className="text-xs text-red-800 mt-1">
                                                                            <span className="font-medium">Action:</span> {testCallResults.commpeak.details.troubleshooting.action}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {testCallResults.commpeak.endpoint && (
                                                                <p className="text-xs text-gray-600 mt-1">
                                                                    <span className="font-medium">Endpoint:</span> {testCallResults.commpeak.endpoint}
                                                                </p>
                                                            )}
                                                            {testCallResults.commpeak.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    <span className="font-medium">HTTP Status:</span> {testCallResults.commpeak.httpStatus}
                                                                </p>
                                                            )}
                                                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                {JSON.stringify(testCallResults.commpeak.details, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}
                                                {/* Technical Details (Expandable) */}
                                                {testCallResults.commpeak.success && testCallResults.commpeak.fullResponse && (
                                                    <details className="mt-3 text-xs">
                                                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                            Technical Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.commpeak.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    HTTP Status: {testCallResults.commpeak.httpStatus}
                                                                </p>
                                                            )}
                                                            {testCallResults.commpeak.fullResponse && (
                                                                <details className="mt-2">
                                                                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                        View Full API Response
                                                                    </summary>
                                                                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                        {JSON.stringify(testCallResults.commpeak.fullResponse, null, 2)}
                                                                    </pre>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* GlobalCall Configuration */}
                    <div className="border-t pt-6 mt-6">
                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold">GlobalCall Integration</h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Configure GlobalCall Click2Call integration
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="globalcall-enabled"
                                            checked={apiKeys.globalcall?.enabled || false}
                                            onCheckedChange={(checked) =>
                                                setApiKeys((prev) => ({
                                                    ...prev,
                                                    globalcall: { ...prev.globalcall, enabled: checked }
                                                }))
                                            }
                                        />
                                        <Label htmlFor="globalcall-enabled">Enable GlobalCall Integration</Label>
                                    </div>
                                </div>

                                {/* Test Connection Section */}
                                {apiKeys.globalcall?.enabled && (
                                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="font-medium mb-3">Test GlobalCall Connection</h4>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Test the GlobalCall integration by initiating a real call. Enter the agent SIP account and destination phone number.
                                        </p>
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <Label>Test SIP Account</Label>
                                                <Input
                                                    placeholder="e.g., agent.brand"
                                                    value={testCallInputs.globalcallSipAccount || ""}
                                                    onChange={(e) =>
                                                        setTestCallInputs((prev) => ({
                                                            ...prev,
                                                            globalcallSipAccount: e.target.value
                                                        }))
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <Label>Test Phone Number</Label>
                                                <Input
                                                    placeholder="e.g., 447700900000"
                                                    value={testCallInputs.globalcallPhone || ""}
                                                    onChange={(e) =>
                                                        setTestCallInputs((prev) => ({
                                                            ...prev,
                                                            globalcallPhone: e.target.value
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => handleTestCall("globalcall")}
                                            disabled={
                                                testCallLoading ||
                                                !testCallInputs.globalcallSipAccount ||
                                                !testCallInputs.globalcallPhone
                                            }
                                            className="w-full sm:w-auto"
                                        >
                                            {testCallLoading && testingProvider === "globalcall" ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Testing Connection...
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4 mr-2" />
                                                    Test Connection
                                                </>
                                            )}
                                        </Button>

                                        {/* Test Results */}
                                        {testCallResults.globalcall && (
                                            <div
                                                className={`mt-4 p-4 rounded-lg ${
                                                    testCallResults.globalcall.success
                                                        ? "bg-green-50 border border-green-200"
                                                        : "bg-red-50 border border-red-200"
                                                }`}
                                            >
                                                <div className="flex items-start">
                                                    {testCallResults.globalcall.success ? (
                                                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                                                    ) : (
                                                        <XCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                                                    )}
                                                    <div className="flex-1">
                                                        <p
                                                            className={`font-medium ${
                                                                testCallResults.globalcall.success
                                                                    ? "text-green-900"
                                                                    : "text-red-900"
                                                            }`}
                                                        >
                                                            {testCallResults.globalcall.success
                                                                ? "✅ Connection successful!"
                                                                : "❌ Connection failed"}
                                                        </p>
                                                        <p
                                                            className={`text-sm mt-1 ${
                                                                testCallResults.globalcall.success
                                                                    ? "text-green-700"
                                                                    : "text-red-700"
                                                            }`}
                                                        >
                                                            {testCallResults.globalcall.message ||
                                                                testCallResults.globalcall.error}
                                                        </p>
                                                        {testCallResults.globalcall.success && (
                                                            <p className="text-xs text-green-600 mt-2">
                                                                Call should appear in your softphone now.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pakistani VoIP (Bubble VoIP) Configuration */}
                    <div className="border-t pt-6 mt-6">
                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold">Pakistani VoIP Integration</h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Configure Bubble VoIP Click2Call integration (HTTP)
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="bubblevoip-enabled"
                                            checked={apiKeys.bubblevoip?.enabled || false}
                                            onCheckedChange={(checked) =>
                                                setApiKeys((prev) => ({
                                                    ...prev,
                                                    bubblevoip: { ...prev.bubblevoip, enabled: checked }
                                                }))
                                            }
                                        />
                                        <Label htmlFor="bubblevoip-enabled">Enable Pakistani VoIP Integration</Label>
                                    </div>
                                </div>

                                {/* Test Connection Section */}
                                {apiKeys.bubblevoip?.enabled && (
                                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="font-medium mb-3">Test Pakistani VoIP Connection</h4>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Test the Bubble VoIP integration by initiating a real call. Enter the agent extension and destination phone number. Your Zoiper should ring when you click Test Call.
                                        </p>
                                        <div className="p-3 bg-blue-50 rounded-md border border-blue-200 mb-4">
                                            <p className="text-xs text-blue-800">
                                                <strong>Note:</strong> This integration uses HTTP (not HTTPS). The endpoint is: <code className="bg-blue-100 px-1 rounded">http://bubble.voip247.cloud/c2c/call.php?ext=XXX&tel=ZZZZZZZZZZZZ</code>
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <Label>Agent Extension</Label>
                                                <Input
                                                    placeholder="e.g., 101"
                                                    value={testCallInputs.agentExtension || ""}
                                                    onChange={(e) =>
                                                        setTestCallInputs((prev) => ({
                                                            ...prev,
                                                            agentExtension: e.target.value
                                                        }))
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <Label>Destination Phone Number</Label>
                                                <Input
                                                    placeholder="e.g., 923001234567"
                                                    value={testCallInputs.destinationPhone || ""}
                                                    onChange={(e) =>
                                                        setTestCallInputs((prev) => ({
                                                            ...prev,
                                                            destinationPhone: e.target.value
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => handleTestCall("bubblevoip")}
                                            disabled={
                                                testCallLoading ||
                                                !testCallInputs.agentExtension ||
                                                !testCallInputs.destinationPhone
                                            }
                                            className="w-full sm:w-auto"
                                        >
                                            {testCallLoading && testingProvider === "bubblevoip" ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Initiating Call...
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4 mr-2" />
                                                    Test Call
                                                </>
                                            )}
                                        </Button>

                                        {/* Test Results */}
                                        {testCallResults.bubblevoip && (
                                            <div
                                                className={`mt-4 p-4 rounded-lg ${
                                                    testCallResults.bubblevoip.success
                                                        ? "bg-green-50 border border-green-200"
                                                        : "bg-red-50 border border-red-200"
                                                }`}
                                            >
                                                <div className="flex items-start">
                                                    {testCallResults.bubblevoip.success ? (
                                                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                                                    ) : (
                                                        <XCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                                                    )}
                                                    <div className="flex-1">
                                                        <p
                                                            className={`font-medium ${
                                                                testCallResults.bubblevoip.success
                                                                    ? "text-green-900"
                                                                    : "text-red-900"
                                                            }`}
                                                        >
                                                            {testCallResults.bubblevoip.success
                                                                ? "✅ Call initiated successfully!"
                                                                : "❌ Call failed"}
                                                        </p>
                                                        <p
                                                            className={`text-sm mt-1 ${
                                                                testCallResults.bubblevoip.success
                                                                    ? "text-green-700"
                                                                    : "text-red-700"
                                                            }`}
                                                        >
                                                            {testCallResults.bubblevoip.message ||
                                                                testCallResults.bubblevoip.error}
                                                        </p>
                                                        {testCallResults.bubblevoip.success && (
                                                            <p className="text-xs text-green-600 mt-2">
                                                                Your Zoiper should ring now. When answered, it will call the destination number.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        {hasUnsavedChanges() && (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                                Unsaved Changes
                            </Badge>
                        )}
                        <Button
                            onClick={handleSaveAPIKeys}
                            disabled={loading || !hasUnsavedChanges()}
                            className=""
                        >
                            <Save className="h-4 w-4 mr-2" />
                            Save Settings
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Affiliate API Keys Section */}
            <Card className="mt-6">
                <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                        <Key className="h-5 w-5 text-green-600" />
                        <span>Affiliate API Keys</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                            Manage API keys for affiliates to submit leads and track their status
                        </p>
                        <Button
                            onClick={() => {
                                setNewApiKeyData({ name: "", defaultStatus: "New", isActive: true, autoAssign: { enabled: false, type: "", deskId: null, userId: null } });
                                setShowCreateApiKeyModal(true);
                            }}
                            className=""
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create API Key
                        </Button>
                    </div>

                    {/* API Keys Table */}
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Affiliate Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">API Key</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Default Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Auto Assign</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Leads</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Last Used</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {affiliateApiKeys.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                            No API keys created yet. Click "Create API Key" to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    affiliateApiKeys.map((key) => (
                                        <tr key={key._id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium">{key.name}</td>
                                            <td className="px-4 py-3 text-sm font-mono text-gray-600">
                                                {key.keyPrefix}••••••••
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <Badge variant="outline">{key.defaultStatus}</Badge>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {key.autoAssign?.enabled ? (
                                                    <div className="space-y-1">
                                                        <Badge className="bg-blue-100 text-blue-800">
                                                            {key.autoAssign.type === "desk" ? "Desk" : key.autoAssign.type === "floorManager" ? "Floor Manager" : "General Manager"}
                                                        </Badge>
                                                        <p className="text-xs text-gray-500">
                                                            {key.autoAssign.type === "desk"
                                                                ? desks.find(d => d._id === key.autoAssign.deskId)?.name || "Unknown desk"
                                                                : key.autoAssign.type === "floorManager"
                                                                    ? (() => { const u = floorManagers.find(u => u._id === key.autoAssign.userId); return u ? `${u.firstName} ${u.lastName}` : "Unknown"; })()
                                                                    : (() => { const u = generalManagers.find(u => u._id === key.autoAssign.userId); return u ? `${u.firstName} ${u.lastName}` : "Unknown"; })()
                                                            }
                                                        </p>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-xs h-6 px-2 text-blue-600"
                                                            onClick={() => setEditingAutoAssign(editingAutoAssign === key._id ? null : key._id)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        {editingAutoAssign === key._id && (
                                                            <div className="mt-2 p-3 border rounded-lg bg-white shadow-sm space-y-2">
                                                                <Select
                                                                    options={[
                                                                        { label: "Desk", value: "desk" },
                                                                        { label: "Floor Manager", value: "floorManager" },
                                                                        { label: "General Manager", value: "generalManager" }
                                                                    ]}
                                                                    value={key.autoAssign.type}
                                                                    onChange={(val) => {
                                                                        handleUpdateApiKey(key._id, {
                                                                            autoAssign: { ...key.autoAssign, type: val, deskId: null, userId: null }
                                                                        });
                                                                    }}
                                                                    placeholder="Assign type"
                                                                />
                                                                {key.autoAssign.type === "desk" && (
                                                                    <Select
                                                                        options={desks.map(d => ({ label: d.name, value: d._id }))}
                                                                        value={key.autoAssign.deskId}
                                                                        onChange={(val) => {
                                                                            handleUpdateApiKey(key._id, {
                                                                                autoAssign: { ...key.autoAssign, deskId: val }
                                                                            });
                                                                        }}
                                                                        placeholder="Select desk"
                                                                        searchable
                                                                    />
                                                                )}
                                                                {key.autoAssign.type === "floorManager" && (
                                                                    <Select
                                                                        options={floorManagers.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u._id }))}
                                                                        value={key.autoAssign.userId}
                                                                        onChange={(val) => {
                                                                            handleUpdateApiKey(key._id, {
                                                                                autoAssign: { ...key.autoAssign, userId: val }
                                                                            });
                                                                        }}
                                                                        placeholder="Select floor manager"
                                                                        searchable
                                                                    />
                                                                )}
                                                                {key.autoAssign.type === "generalManager" && (
                                                                    <Select
                                                                        options={generalManagers.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u._id }))}
                                                                        value={key.autoAssign.userId}
                                                                        onChange={(val) => {
                                                                            handleUpdateApiKey(key._id, {
                                                                                autoAssign: { ...key.autoAssign, userId: val }
                                                                            });
                                                                        }}
                                                                        placeholder="Select general manager"
                                                                        searchable
                                                                    />
                                                                )}
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-xs text-red-600"
                                                                    onClick={() => {
                                                                        handleUpdateApiKey(key._id, {
                                                                            autoAssign: { enabled: false, type: "", deskId: null, userId: null }
                                                                        });
                                                                        setEditingAutoAssign(null);
                                                                    }}
                                                                >
                                                                    Disable Auto-Assign
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Badge className="bg-gray-100 text-gray-600">None</Badge>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-xs h-6 px-2 ml-1 text-blue-600"
                                                            onClick={() => setEditingAutoAssign(editingAutoAssign === key._id ? null : key._id)}
                                                        >
                                                            Set up
                                                        </Button>
                                                        {editingAutoAssign === key._id && (
                                                            <div className="mt-2 p-3 border rounded-lg bg-white shadow-sm space-y-2">
                                                                <Select
                                                                    options={[
                                                                        { label: "Desk", value: "desk" },
                                                                        { label: "Floor Manager", value: "floorManager" },
                                                                        { label: "General Manager", value: "generalManager" }
                                                                    ]}
                                                                    value=""
                                                                    onChange={(val) => {
                                                                        handleUpdateApiKey(key._id, {
                                                                            autoAssign: { enabled: true, type: val, deskId: null, userId: null }
                                                                        });
                                                                    }}
                                                                    placeholder="Assign type"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {key.isActive ? (
                                                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                                                ) : (
                                                    <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{key.leadCount || 0}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {key.lastUsedAt
                                                    ? new Date(key.lastUsedAt).toLocaleDateString()
                                                    : "Never"}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="flex items-center space-x-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleUpdateApiKey(key._id, { isActive: !key.isActive })}
                                                        disabled={apiKeyLoading}
                                                    >
                                                        {key.isActive ? "Deactivate" : "Activate"}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleRegenerateApiKey(key._id)}
                                                        disabled={apiKeyLoading}
                                                    >
                                                        <RefreshCw className="h-3 w-3" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDeleteApiKey(key._id)}
                                                        disabled={apiKeyLoading}
                                                        className="text-red-600 hover:text-red-700"
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* API Documentation Info */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-blue-900 mb-2">API Endpoints for Affiliates</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• <strong>Create Lead:</strong> POST /lead/v1/public (Header: Authorization: Bearer {'<api-key>'})</li>
                            <li>• <strong>Get My Leads:</strong> GET /lead/v1/public/my-leads</li>
                            <li>• <strong>Get Lead Status:</strong> GET /lead/v1/public/status/:leadId</li>
                        </ul>
                    </div>
                </CardContent>
            </Card>

            {/* Affiliate Notification Settings */}
            {affiliateApiKeys.length > 0 && (
                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <Bell className="h-5 w-5 text-orange-600" />
                            <span>Affiliate Notification Settings</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Control which affiliates send notifications when new leads are received. Muted affiliates will not trigger notifications to admins.
                        </p>
                        
                        <div className="border rounded-lg divide-y">
                            {affiliateApiKeys.map((key) => {
                                const isMuted = mutedAffiliates.includes(key.name);
                                return (
                                    <div key={key._id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                                        <div className="flex items-center space-x-3">
                                            {isMuted ? (
                                                <BellOff className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <Bell className="h-5 w-5 text-orange-600" />
                                            )}
                                            <div>
                                                <p className="font-medium text-gray-900">{key.name}</p>
                                                <p className="text-sm text-gray-500">
                                                    {isMuted ? "Notifications muted" : "Notifications active"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            {isMuted && (
                                                <Badge variant="outline" className="text-gray-600 border-gray-300">
                                                    Muted
                                                </Badge>
                                            )}
                                            <Switch
                                                checked={!isMuted}
                                                onCheckedChange={() => handleToggleAffiliateNotification(key.name)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end pt-4 border-t">
                            <Button
                                onClick={handleSaveNotificationSettings}
                                disabled={loading || JSON.stringify(mutedAffiliates.sort()) === JSON.stringify(originalMutedAffiliates.sort())}
                                className=""
                            >
                                <Save className="h-4 w-4 mr-2" />
                                Save Notification Settings
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit API Key Modal */}
            <Dialog open={showCreateApiKeyModal} onOpenChange={handleCloseApiKeyModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{generatedApiKey ? "API Key Created" : "Create New API Key"}</DialogTitle>
                        <DialogDescription>
                            {generatedApiKey
                                ? "Copy this API key now. You won't be able to see it again!"
                                : "Generate a new API key for an affiliate. The key will be shown only once."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {!generatedApiKey ? (
                            <>
                                <div>
                                    <Label htmlFor="affiliate-name">Affiliate Name *</Label>
                                    <Input
                                        id="affiliate-name"
                                        value={newApiKeyData.name}
                                        onChange={(e) => setNewApiKeyData({ ...newApiKeyData, name: e.target.value })}
                                        placeholder="e.g., PartnerABC"
                                        disabled={apiKeyLoading}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="default-status">Default Status</Label>
                                    <Select
                                        options={leadStatuses.length > 0
                                            ? leadStatuses.map((status) => ({
                                                label: status.label || status.value || status,
                                                value: status.value || status.label || status
                                            }))
                                            : [
                                                { label: "New", value: "New" },
                                                { label: "Interested", value: "Interested" },
                                                { label: "Follow-up", value: "Follow-up" },
                                                { label: "Converted", value: "Converted" },
                                                { label: "Not Answered", value: "Not Answered" }
                                            ]}
                                        value={newApiKeyData.defaultStatus}
                                        onChange={(value) => setNewApiKeyData({ ...newApiKeyData, defaultStatus: value })}
                                        placeholder="Select default status"
                                        disabled={apiKeyLoading}
                                    />
                                </div>
                                {/* Auto-Assign Section */}
                                <div className="border rounded-lg p-3 space-y-3 bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm font-medium">Auto-Assign Leads</Label>
                                        <Switch
                                            checked={newApiKeyData.autoAssign?.enabled || false}
                                            onCheckedChange={(checked) => setNewApiKeyData({
                                                ...newApiKeyData,
                                                autoAssign: { ...newApiKeyData.autoAssign, enabled: checked, type: checked ? newApiKeyData.autoAssign?.type || "" : "", deskId: null, userId: null }
                                            })}
                                            disabled={apiKeyLoading}
                                        />
                                    </div>
                                    {newApiKeyData.autoAssign?.enabled && (
                                        <>
                                            <div>
                                                <Label className="text-xs text-gray-600">Assign To</Label>
                                                <Select
                                                    options={[
                                                        { label: "Desk", value: "desk" },
                                                        { label: "Floor Manager", value: "floorManager" },
                                                        { label: "General Manager", value: "generalManager" }
                                                    ]}
                                                    value={newApiKeyData.autoAssign?.type || ""}
                                                    onChange={(value) => setNewApiKeyData({
                                                        ...newApiKeyData,
                                                        autoAssign: { ...newApiKeyData.autoAssign, type: value, deskId: null, userId: null }
                                                    })}
                                                    placeholder="Select assign type"
                                                    disabled={apiKeyLoading}
                                                />
                                            </div>
                                            {newApiKeyData.autoAssign?.type === "desk" && (
                                                <div>
                                                    <Label className="text-xs text-gray-600">Select Desk</Label>
                                                    <Select
                                                        options={desks.map(d => ({ label: d.name, value: d._id }))}
                                                        value={newApiKeyData.autoAssign?.deskId || ""}
                                                        onChange={(value) => setNewApiKeyData({
                                                            ...newApiKeyData,
                                                            autoAssign: { ...newApiKeyData.autoAssign, deskId: value }
                                                        })}
                                                        placeholder="Select a desk"
                                                        searchable
                                                        disabled={apiKeyLoading}
                                                    />
                                                </div>
                                            )}
                                            {newApiKeyData.autoAssign?.type === "floorManager" && (
                                                <div>
                                                    <Label className="text-xs text-gray-600">Select Floor Manager</Label>
                                                    <Select
                                                        options={floorManagers.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u._id }))}
                                                        value={newApiKeyData.autoAssign?.userId || ""}
                                                        onChange={(value) => setNewApiKeyData({
                                                            ...newApiKeyData,
                                                            autoAssign: { ...newApiKeyData.autoAssign, userId: value }
                                                        })}
                                                        placeholder="Select a floor manager"
                                                        searchable
                                                        disabled={apiKeyLoading}
                                                    />
                                                </div>
                                            )}
                                            {newApiKeyData.autoAssign?.type === "generalManager" && (
                                                <div>
                                                    <Label className="text-xs text-gray-600">Select General Manager</Label>
                                                    <Select
                                                        options={generalManagers.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u._id }))}
                                                        value={newApiKeyData.autoAssign?.userId || ""}
                                                        onChange={(value) => setNewApiKeyData({
                                                            ...newApiKeyData,
                                                            autoAssign: { ...newApiKeyData.autoAssign, userId: value }
                                                        })}
                                                        placeholder="Select a general manager"
                                                        searchable
                                                        disabled={apiKeyLoading}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="flex space-x-4 pt-4">
                                    <Button
                                        onClick={handleCreateApiKey}
                                        disabled={apiKeyLoading || !newApiKeyData.name.trim()}
                                        className="flex-1"
                                    >
                                        {apiKeyLoading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            "Create API Key"
                                        )}
                                    </Button>
                                    <Button variant="outline" onClick={handleCloseApiKeyModal} disabled={apiKeyLoading}>
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <Label className="text-green-900 font-medium mb-2 block">
                                        ⚠️ Copy this API key now. You won't be able to see it again!
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            value={generatedApiKey}
                                            readOnly
                                            className="font-mono text-sm"
                                        />
                                        <Button
                                            onClick={() => copyToClipboard(generatedApiKey)}
                                            size="sm"
                                            variant="outline"
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                </div>
                                <Button
                                    onClick={handleCloseApiKeyModal}
                                    className="w-full"
                                >
                                    Done
                                </Button>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Database Backup & Restore Section */}
            <Card className="mt-6">
                <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                        <Database className="h-5 w-5 text-purple-600" />
                        <span>Database Backup & Restore</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            <p className="text-sm text-yellow-800 font-medium">
                                Important: Always create a backup before making major changes to your system.
                            </p>
                        </div>
                        <p className="text-xs text-yellow-700 mt-2">
                            Store backups in a safe location. In case of data loss, you can restore from the most recent backup.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Download Backup */}
                        <div className="border rounded-lg p-6 space-y-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <Download className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">Download Backup</h3>
                                    <p className="text-sm text-gray-600">
                                        Export all database data to a single JSON file
                                    </p>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">What's Included:</h4>
                                <ul className="text-xs text-gray-600 space-y-1">
                                    <li>✓ All users and permissions</li>
                                    <li>✓ All leads and their data</li>
                                    <li>✓ All desks and assignments</li>
                                    <li>✓ All positions and configurations</li>
                                    <li>✓ System settings and API keys</li>
                                    <li>✓ Call logs and activity history</li>
                                </ul>
                            </div>

                            <Button
                                onClick={handleDownloadBackup}
                                disabled={backupLoading}
                                className="w-full"
                            >
                                {backupLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Creating Backup...
                                    </>
                                ) : (
                                    <>
                                        <Download className="h-4 w-4 mr-2" />
                                        Download Database Backup
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Upload Backup */}
                        <div className="border rounded-lg p-6 space-y-4 border-red-200">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                    <Upload className="h-5 w-5 text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">Restore Backup</h3>
                                    <p className="text-sm text-gray-600">
                                        Upload a backup file to restore your database
                                    </p>
                                </div>
                            </div>

                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <h4 className="text-sm font-medium text-red-900 mb-2">⚠️ Warning:</h4>
                                <ul className="text-xs text-red-800 space-y-1">
                                    <li>• <strong>Merge mode:</strong> Combines backup with existing data</li>
                                    <li>• <strong>Replace mode:</strong> DELETES all data first (dangerous!)</li>
                                    <li>• Always download a backup before restoring</li>
                                    <li>• This operation cannot be undone</li>
                                </ul>
                            </div>

                            <div>
                                <input
                                    type="file"
                                    accept=".json"
                                    onChange={handleUploadBackup}
                                    disabled={restoreLoading}
                                    className="hidden"
                                    id="backup-upload-input"
                                />
                                <Button
                                    onClick={() => document.getElementById("backup-upload-input").click()}
                                    disabled={restoreLoading}
                                    variant="outline"
                                    className="w-full border-red-600 text-red-600 hover:bg-red-50"
                                >
                                    {restoreLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Restoring...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-4 w-4 mr-2" />
                                            Upload & Restore Backup
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-blue-900 mb-2">Backup Best Practices</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Create daily backups and store them in a secure location</li>
                            <li>• Keep multiple backup versions (at least 7 days)</li>
                            <li>• Test restore process periodically to ensure backups work</li>
                            <li>• Download a fresh backup before any major system changes</li>
                            <li>• Store backups outside of your server (cloud storage, external drive)</li>
                        </ul>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
